<template>
  <div
    v-if="isEditMode"
    v-bind="$attrs"
    :id="props.uuid"
    class="p-6 md:p-8 bg-white rounded-xl"
    :style="{ scrollMarginTop: pageEditorStore.scrollMarginTop }"
  >
    <div class="font-semibold text-gray-700 mb-3">Floating banner preview</div>

    <div
      v-if="allItems.length"
      class="max-w-full mx-auto"
      :style="bannerWidthStyle"
    >
      <FloatingBannerContent
        :form-data="props.formData"
        :items="previewRenderItems"
      />
    </div>

    <template v-for="(item, index) in allItems" :key="index">
      <div
        v-if="getTargetWarning(item)"
        class="m-1 mt-2 rounded-lg bg-amber-50 border border-amber-300 px-3 py-2 text-xs text-amber-900"
      >
        Banner item {{ index + 1 }}: {{ getTargetWarning(item) }}
      </div>
    </template>

    <div
      v-if="!allItems.length"
      class="rounded-lg bg-amber-50 border border-amber-300 px-3 py-2 text-sm text-amber-900"
    >
      Add at least one item to display the floating banner.
    </div>
  </div>

  <FloatingBannerRuntimeStack
    v-else-if="shouldRenderRuntime"
    v-bind="$attrs"
    :banners="runtimeBanners"
    :visible="isPopupVisible"
    @close="isPopupClosed = true"
    @item-click="onItemClick"
  />
</template>

<script setup lang="ts">
import debounce from 'lodash-es/debounce'
import FloatingBannerContent from './FloatingBannerContent.vue'
import FloatingBannerRuntimeStack from './FloatingBannerRuntimeStack.vue'
import type {
  FloatingBannerFormData,
  FloatingBannerItem,
  FloatingBannerRenderItem,
  FloatingBannerRuntimeBanner,
} from './types'
import { useEvents } from '~/stores/events'
import { useOrders } from '~/stores/orders'
import { BlockType, usePageEditor } from '~/stores/page-editor'

defineOptions({ inheritAttrs: false })

interface PropsInterface {
  uuid: string
  blockUuid: string
  formData: FloatingBannerFormData
  isInIframe?: boolean
}

interface FloatingBannerSource {
  key: string
  formData: FloatingBannerFormData
}

const props = withDefaults(defineProps<PropsInterface>(), {
  isInIframe: false,
})

const SCROLL_TRIGGER = 200

const eventsStore = useEvents()
const ordersStore = useOrders()
const pageEditorStore = usePageEditor()
const route = useRoute()
const { selectedTickets } = storeToRefs(ordersStore)
const { getBlockLink, getLinkTarget, getLinkUrl } = useEditorLinks()

const isMounted = ref(false)
const isScrolled = ref(false)
const isPopupClosed = ref(false)
const isTargetsReady = ref(false)
const unavailableTargetHashes = shallowRef<Set<string>>(new Set())
const intersectingTargets = shallowRef<Set<Element>>(new Set())

let targetObserver: IntersectionObserver | null = null
let targetObserverGeneration = 0
let isScrollListenerActive = false

const isEditMode = computed(() => pageEditorStore.isEditMode)
const isIframeMode = computed(
  () => props.isInIframe || pageEditorStore.isInIframe
)
const allItems = computed(() => props.formData.items)
const eventSlug = computed(() => {
  const routeEvent = route.params.event
  return Array.isArray(routeEvent) ? routeEvent[0] : routeEvent
})
const isCurrentEvent = computed(
  () => !!eventSlug.value && eventsStore.event?.slug === String(eventSlug.value)
)
const hasTicket = computed(
  () => isCurrentEvent.value && !!eventsStore.event?.is_has_ticket
)
const hasTicketsBlock = computed(() =>
  pageEditorStore.editorBlocks.some(
    (block) => block.type === BlockType.Tickets && block.is_visible
  )
)
const hasSelectedTickets = computed(
  () =>
    isCurrentEvent.value &&
    hasTicketsBlock.value &&
    Object.values(selectedTickets.value).some((quantity) => quantity > 0)
)
const isEventContextReady = computed(
  () => !eventSlug.value || isCurrentEvent.value
)

const blocksByHash = computed(() => {
  return new Map(
    pageEditorStore.editorBlocks.map((block) => [
      pageEditorStore.getBlockId(block),
      block,
    ])
  )
})

const visibleFloatingBannerBlocks = computed(() => {
  return pageEditorStore.editorBlocks.filter(
    (block) => block.type === BlockType.FloatingBanner && block.is_visible
  )
})

const isRuntimeStackHost = computed(() => {
  return visibleFloatingBannerBlocks.value[0]?.uuid === props.blockUuid
})

const runtimeBannerSources = computed<FloatingBannerSource[]>(() => {
  if (!isRuntimeStackHost.value || isEditMode.value || isIframeMode.value) {
    return []
  }

  return visibleFloatingBannerBlocks.value.map((block) => ({
    key: block.uuid,
    formData: block.editorData as FloatingBannerFormData,
  }))
})

const internalTargetHashes = computed(() => {
  return [
    ...new Set(
      runtimeBannerSources.value
        .flatMap((banner) => getEligibleItems(banner.formData.items))
        .filter((item) => getTargetType(item) === 'block')
        .map((item) => item.blockHash?.trim())
        .filter((hash): hash is string => !!hash)
    ),
  ]
})

const previewRenderItems = computed(() => {
  return allItems.value.map((item) => createRenderItem(item, false))
})

const runtimeBanners = computed<FloatingBannerRuntimeBanner[]>(() => {
  return runtimeBannerSources.value
    .map((banner) => ({
      ...banner,
      items: getRuntimeItems(banner.formData.items).map((item) =>
        createRenderItem(item, true)
      ),
    }))
    .filter((banner) => banner.items.length > 0)
})

const bannerWidthStyle = computed(() => ({
  width: `${props.formData.width}px`,
}))

const shouldRenderRuntime = computed(() => {
  return (
    isMounted.value &&
    isRuntimeStackHost.value &&
    !isEditMode.value &&
    !isIframeMode.value &&
    isEventContextReady.value &&
    isTargetsReady.value &&
    runtimeBanners.value.length > 0
  )
})

const isPopupVisible = computed(() => {
  return (
    shouldRenderRuntime.value &&
    isScrolled.value &&
    !isPopupClosed.value &&
    intersectingTargets.value.size === 0
  )
})

function getTargetType(item: FloatingBannerItem) {
  return item.targetType
}

function getParsedLink(link: string): URL | null {
  try {
    return new URL(
      link,
      process.client ? window.location.href : 'https://internal.invalid'
    )
  } catch {
    return null
  }
}

function isValidLink(link: string): boolean {
  const parsedLink = getParsedLink(link)
  return !!parsedLink && ['http:', 'https:'].includes(parsedLink.protocol)
}

function isExternalLink(link: string): boolean {
  const parsedLink = getParsedLink(link)

  return (
    !!parsedLink &&
    !!process.client &&
    parsedLink.origin !== window.location.origin
  )
}

function getTargetWarning(item: FloatingBannerItem): string {
  if (getTargetType(item) === 'none') {
    return ''
  }

  if (getTargetType(item) === 'link') {
    const link = item.link?.trim()

    if (!link) {
      return 'Link is not set. This item will be hidden.'
    }

    return isValidLink(link)
      ? ''
      : 'Enter a valid link. This item will be hidden.'
  }

  const blockHash = item.blockHash?.trim()

  if (!blockHash) {
    return 'Target page block is not selected. This item will be hidden.'
  }

  const block = blocksByHash.value.get(blockHash)

  if (!block) {
    return `Target page block “${blockHash}” was not found. This item will be hidden.`
  }

  if (!block.is_visible) {
    return `Target page block “${blockHash}” is hidden. This item will be hidden.`
  }

  if (block.type === BlockType.FloatingBanner) {
    return 'A “Floating banner” block cannot be used as a target. This item will be hidden.'
  }

  return ''
}

function isItemAllowedForAudience(item: FloatingBannerItem): boolean {
  if (hasTicket.value && item.hideWhenHasTicket) {
    return false
  }

  return !hasSelectedTickets.value || !item.hideWhenTicketsSelected
}

function getEligibleItems(items: FloatingBannerItem[]): FloatingBannerItem[] {
  return items.filter(
    (item) => isItemAllowedForAudience(item) && !getTargetWarning(item)
  )
}

function getRuntimeItems(items: FloatingBannerItem[]): FloatingBannerItem[] {
  return getEligibleItems(items).filter((item) => {
    if (getTargetType(item) !== 'block') {
      return true
    }

    const blockHash = item.blockHash?.trim()
    return !!blockHash && !unavailableTargetHashes.value.has(blockHash)
  })
}

function createStaticRenderItem(
  item: FloatingBannerItem
): FloatingBannerRenderItem {
  return {
    source: item,
    element: 'div',
    attrs: {},
    interactive: false,
  }
}

function createRenderItem(
  item: FloatingBannerItem,
  interactive: boolean
): FloatingBannerRenderItem {
  if (!interactive || getTargetType(item) === 'none') {
    return createStaticRenderItem(item)
  }

  if (getTargetType(item) === 'link') {
    const link = item.link?.trim()

    if (!link) {
      return createStaticRenderItem(item)
    }

    return {
      source: item,
      element: 'a',
      attrs: {
        href: getLinkUrl(link),
        target: getLinkTarget(link, isExternalLink(link)),
        rel: 'noopener noreferrer',
        'aria-label': item.text || 'Open banner link',
      },
      interactive: true,
    }
  }

  const blockHash = item.blockHash?.trim()

  if (!blockHash) {
    return createStaticRenderItem(item)
  }

  return {
    source: item,
    element: 'a',
    attrs: {
      href: getBlockLink(blockHash),
      'aria-label': item.text || 'Go to banner target block',
    },
    interactive: true,
  }
}

function onItemClick(item: FloatingBannerItem, event: MouseEvent) {
  if (isEditMode.value || getTargetType(item) !== 'block') {
    return
  }

  const blockHash = item.blockHash?.trim()
  const targetElement = blockHash ? document.getElementById(blockHash) : null

  if (!targetElement) {
    event.preventDefault()
    return
  }

  if (
    event.button !== 0 ||
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey
  ) {
    return
  }

  event.preventDefault()
  targetElement.scrollIntoView({ behavior: 'smooth' })
}

function updateScrollState() {
  isScrolled.value = window.scrollY > SCROLL_TRIGGER
}

const onScroll = debounce(updateScrollState, 150)

function disconnectTargetObserver() {
  targetObserver?.disconnect()
  targetObserver = null
  intersectingTargets.value = new Set()
}

async function syncTargetObserver() {
  const generation = ++targetObserverGeneration
  disconnectTargetObserver()
  unavailableTargetHashes.value = new Set()

  if (
    !isMounted.value ||
    !isRuntimeStackHost.value ||
    isEditMode.value ||
    isIframeMode.value
  ) {
    isTargetsReady.value = true
    return
  }

  isTargetsReady.value = false
  await nextTick()

  if (generation !== targetObserverGeneration) {
    return
  }

  const unavailableHashes = new Set<string>()
  const targetElements = new Set<Element>()

  internalTargetHashes.value.forEach((hash) => {
    const element = document.getElementById(hash)

    if (element) {
      targetElements.add(element)
    } else {
      unavailableHashes.add(hash)
    }
  })

  unavailableTargetHashes.value = unavailableHashes

  if (!targetElements.size) {
    isTargetsReady.value = true
    return
  }

  const pendingInitialTargets = new Set(targetElements)

  targetObserver = new IntersectionObserver((entries) => {
    if (generation !== targetObserverGeneration) {
      return
    }

    const nextIntersectingTargets = new Set(intersectingTargets.value)

    entries.forEach((entry) => {
      pendingInitialTargets.delete(entry.target)

      if (entry.isIntersecting) {
        nextIntersectingTargets.add(entry.target)
      } else {
        nextIntersectingTargets.delete(entry.target)
      }
    })

    intersectingTargets.value = nextIntersectingTargets
    isTargetsReady.value = pendingInitialTargets.size === 0
  })

  targetElements.forEach((element) => targetObserver?.observe(element))
}

function configureRuntimeBehavior() {
  if (!isMounted.value) {
    return
  }

  const shouldRun =
    isRuntimeStackHost.value && !isEditMode.value && !isIframeMode.value

  if (shouldRun && !isScrollListenerActive) {
    updateScrollState()
    window.addEventListener('scroll', onScroll, { passive: true })
    isScrollListenerActive = true
  } else if (!shouldRun && isScrollListenerActive) {
    window.removeEventListener('scroll', onScroll)
    onScroll.cancel()
    isScrollListenerActive = false
  }

  syncTargetObserver()
}

watch([isEditMode, isRuntimeStackHost], configureRuntimeBehavior)
watch(internalTargetHashes, syncTargetObserver, { deep: true })

onMounted(() => {
  isMounted.value = true
  configureRuntimeBehavior()
})

onUnmounted(() => {
  isMounted.value = false
  targetObserverGeneration += 1
  disconnectTargetObserver()

  if (isScrollListenerActive) {
    window.removeEventListener('scroll', onScroll)
    isScrollListenerActive = false
  }

  onScroll.cancel()
})
</script>
